import * as angular from "angular";
import {IController} from "angular";
import {RuleEventModel} from "../../models/rule-event.model";
import {Rule} from "../../models/rule.model";
import {RuleControllerStage} from "../../models/rule-controller-stage.model";
import {RuleEngineService} from "../../services/rule-engine.service";
import {RuleFilterModel} from "../../models/rule-filter.model";
import {RuleActionModel} from "../../models/rule-action.model";
import {RulesService} from "../../services/rules.service";
import {SplitService} from "../../../commons/services/split-service.service";
import {CommonsAlertsService} from "../../../commons/services/commons-alerts.service";
import {RuleTypeEnum} from "../../types/rule-type.enum";

export class RuleViewComponent implements IController {

  events: RuleEventModel[];
  metadataSettings: any;
  rule: Rule;

  public entitiesMap: {} = {};
  public ruleEntityFilters: RuleFilterModel[];
  public ruleEntityActions: RuleActionModel[];
  public isEditMode: boolean;
  public metaData: any[];
  public entitySubtypes: string[];
  public ruleTypeEnum: typeof RuleTypeEnum = RuleTypeEnum;
  public currentDate: number;
  public cronMinutes: number;
  public cronHours: number;
  public cronDayOfMonth: number;
  public cronMonth: number;
  public cronDayOfWeek: number;
  public scheduledTaskDefaultDate: number;
  public scheduledTaskInfoStartTimeCopy: any;

  constructor(private ruleEngineService: RuleEngineService, private rulesService: RulesService,
              private $state, private commonsAlertsService: CommonsAlertsService, private splitService: SplitService, private moment) {
  }

  $onInit() {
    this.currentDate = this.moment().startOf('day').valueOf();
    this.scheduledTaskDefaultDate = this.moment().startOf('day').add(1, 'days').valueOf();

    this._initRuleEntitiesMap();
    if (!this.rule) {
      this._initRule();
    } else {
      if (this.rule.ruleType === this.ruleTypeEnum.ScheduledTask) {
        this.parseCronExpression();
      }
      this.isEditMode = true;
      this._getEntityActions();
      this._getEntityFilters();
      this._getEntityMetaData();
      this._getEntitySubtypes();
    }

  }

  onSelectEvent() {
    if (this.rule.eventName === this.ruleTypeEnum.ScheduledTask) {
      this.rule.ruleType = this.ruleTypeEnum.ScheduledTask;
      this.rule.ruleEntityType = 'None';
      this.rule.scheduledTaskInfo = {};

      this.rule.scheduledTaskInfo.startTime = this.scheduledTaskDefaultDate;

      this._getEntityActions();
    } else {
      this.rule.ruleType = this.ruleTypeEnum.Entity;
      this.rule.scheduledTaskInfo = undefined;
      this.rule.ruleEntityType = undefined;
    }
  }

  onAddController() {
    const controller: RuleControllerStage = {
      actions: [{
        actionType: undefined,
        dataList: []
      }],
      conditionOperation: {
        operationType: 'Condition',
        type: 'and',
        conditions: []
      },
      operationType: 'Controller',
      sequence: this.rule.stages[1].operations.length + 1
    }

    this.rule.stages[1].operations.push(controller);
    this._renumberControllerSequences();
  }

  onRemoveController(index: number) {
    this.rule.stages[1].operations.splice(index, 1);
    this._renumberControllerSequences();
  }

  private _renumberControllerSequences() {
    this.rule.stages[1].operations.forEach((operation: RuleControllerStage, index: number) => {
      operation.sequence = index + 1;
    });
  }

  onSelectEntity() {
    this.rule.stages[1].operations = [];
    // this._initRule();
    this._getEntityActions();
    this._getEntityFilters();
    this._getEntityMetaData();
    this._getEntitySubtypes();
  }

  onChangeScheduleTaskEventTimeZoneId(zoneId) {
    this.rule.scheduledTaskInfo.zoneId = zoneId;
  }

  onChangeScheduleTaskInfoStartTime(date) {
    this.rule.scheduledTaskInfo.startTime = this.moment(date).startOf('day').valueOf();
  }

  onChangeScheduleTaskInfoRepeatUntil(date) {
    this.rule.scheduledTaskInfo.repeatUntil = this.moment(date).endOf('day').valueOf();
  }

  onEditScheduledTaskStartTime() {
    this.scheduledTaskInfoStartTimeCopy = angular.copy(this.rule.scheduledTaskInfo.startTime);
    this.rule.scheduledTaskInfo.startTime = this.scheduledTaskDefaultDate;
  }

  onCancelEditingScheduledTaskStartTime() {
    this.rule.scheduledTaskInfo.startTime = angular.copy(this.scheduledTaskInfoStartTimeCopy);
    this.scheduledTaskInfoStartTimeCopy = undefined;
  }

  onCancel() {
    this.$state.go('rules.table');
  }

  onSave(form) {
    form.$setSubmitted(true);
    if (form.$valid) {
      if (this.rule.ruleType === this.ruleTypeEnum.ScheduledTask) {
        this.rule.scheduledTaskInfo.cronExpression = this.generateCronExpression();
      }
      this._renumberControllerSequences();
      this.rulesService.createRule(this.rule)
        .then(res => {
          this.$state.go('rules.table');
        });
    } else {
      this.commonsAlertsService.formInvalidInfo();
    }
  }

  onUpdate(form) {
    form.$setSubmitted(true);
    if (form.$valid) {
      if (!this.rule.ruleType) {
        this.rule.ruleType = this.ruleTypeEnum.Entity;
      }
      if (this.rule.ruleType === this.ruleTypeEnum.ScheduledTask) {
        this.rule.scheduledTaskInfo.cronExpression = this.generateCronExpression();
      }
      this._renumberControllerSequences();
      this.rulesService.updateRule(this.rule)
        .then(res => {
          this.$state.go('rules.table');
        });
    } else {
      this.commonsAlertsService.formInvalidInfo();
    }
  }

  onChangeFilters(filters) {
    this.rule.stages[0].operations[0] = angular.copy(filters);
  }

  onSelectCronHour(time) {
    this.cronHours = time;
  }

  private _getEntityMetaData() {
    if (this.rule.ruleEntityType === 'OmsOrder') {
      this.metaData = this.metadataSettings.filter(m => m.holder === 'OMS');
    } else if (this.rule.ruleEntityType === 'FulfillmentStep') {
      this.metaData = this.metadataSettings.filter(m => m.holder === 'FULFILLMENT_STEP');
    } else {
      this.metaData = this.metadataSettings;
    }
  }

  private _getEntityFilters() {
    this.ruleEntityFilters = undefined;
    this.ruleEngineService.getEntityFilters(this.rule.ruleEntityType)
      .then((res: any) => {
        if (res?.length) {
          this.ruleEntityFilters = res[0].fields;
        }
      })
  }

  private _getEntityActions() {
    this.ruleEntityActions = undefined;
    this.ruleEngineService.getEntityActions(this.rule.ruleEntityType)
      .then((res: any[]) => {
        if (res?.length) {
          this.ruleEntityActions = res[0].actions;
        }
      })
  }

  private _getEntitySubtypes() {
    this.entitySubtypes = undefined;
    this.ruleEngineService.getEntitySubtypes(this.rule.ruleEntityType)
      .then((res: any) => {
        if (res?.length) {
          this.entitySubtypes = res[0].convertOptions;
        }
      })
  }

  private _initRuleEntitiesMap() {
    this.events.forEach((e: RuleEventModel) => {
      this.entitiesMap[e.eventName] = e.entityDataList;
    });
  }

  private parseCronExpression() {
    const cronParsedValues = this.rule.scheduledTaskInfo.cronExpression.split(' ');
    this.cronMinutes = cronParsedValues[1] === "*" ? undefined : parseInt(cronParsedValues[1]);
    this.cronHours = cronParsedValues[2] === "*" ? undefined : this.moment().hour(parseInt(cronParsedValues[2])).minute(0).seconds(0).valueOf();
    this.cronDayOfMonth = cronParsedValues[3] === "*" ? undefined : parseInt(cronParsedValues[3]);
    this.cronMonth = cronParsedValues[4] === "*" ? undefined : parseInt(cronParsedValues[4]);
    this.cronDayOfWeek = cronParsedValues[5] === "*" ? undefined : parseInt(cronParsedValues[5]);
  }

  private generateCronExpression() {
    return `0 ${this.cronMinutes || '*'} ${this.moment(this.cronHours).get('hour') || '*'} ${this.cronDayOfMonth || '*'} ${this.cronMonth || '*'} ${this.cronDayOfWeek || '*'}`;
  }


  private _initRule() {
    this.rule = {
      name: undefined,
      description: undefined,
      ruleEntityType: undefined,
      eventName: undefined,
      stages: [
        {
          sequence: 0,
          description: undefined,
          operationType: "Condition",
          operations: [
            {
              operationType: "Condition",
              type: "and",
              conditions: []
            }
          ]
        },
        {
          sequence: 1,
          operationType: "Controller",
          description: "",
          operations: []
        }
      ],
      isActive: false
    }
  }

}


angular
  .module('bringoz.admin.ruleEngine')
  .component('ruleView', {
    bindings: {
      events: '<',
      metadataSettings: '<',
      rule: '<?',
    },
    template: require('./rule-view.html').default,
    controller: RuleViewComponent
  });

